import { useEffect, useState } from "react";
import {
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
} from "firebase/auth";
import { auth, googleProvider } from "./firebase";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";

export default function SignIn() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSignup, setIsSignup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Redirect if already logged in
  useEffect(() => {
    if (user) navigate("/app");
  }, [user, navigate]);

  // Email + Password auth
  const handleEmailAuth = async () => {
    setError("");
    setLoading(true);

    try {
      if (isSignup) {
        await createUserWithEmailAndPassword(auth, email, password);
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Google auth
  const handleGoogleSignIn = async () => {
    setError("");
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (err) {
      setError("Google sign-in failed");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-black relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-black">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `
              linear-gradient(rgba(139, 92, 246, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(139, 92, 246, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: "50px 50px",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-violet-600/10 via-black to-purple-900/10" />
      </div>

      {/* Card */}
      <div className="relative z-10 w-full max-w-md px-6">
        <div className="bg-zinc-900/50 backdrop-blur-sm border border-zinc-800 rounded-2xl p-8 shadow-2xl">

          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">
              NexNotes AI
            </h1>
            <p className="text-zinc-400 text-sm">
              {isSignup ? "Create your account" : "Sign in to your account"}
            </p>
          </div>

          <div className="space-y-4">
            {/* Email */}
            <div>
              <label className="block text-sm text-zinc-300 mb-2">
                Email address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value.trim())}
                placeholder="name@company.com"
                className="w-full px-4 py-3 bg-black border border-zinc-700 rounded-lg text-white"
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm text-zinc-300 mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Minimum 6 characters"
                className="w-full px-4 py-3 bg-black border border-zinc-700 rounded-lg text-white"
              />
            </div>

            {/* Error */}
            {error && (
              <p className="text-sm text-red-500">{error}</p>
            )}

            {/* Email Auth Button */}
            <button
              onClick={handleEmailAuth}
              disabled={loading}
              className="w-full py-3 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition"
            >
              {loading
                ? "Please wait..."
                : isSignup
                ? "Create account"
                : "Sign in"}
            </button>

            {/* Toggle */}
            <p
              onClick={() => setIsSignup(!isSignup)}
              className="text-center text-sm text-zinc-400 cursor-pointer hover:text-white"
            >
              {isSignup
                ? "Already have an account? Sign in"
                : "New here? Create an account"}
            </p>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-zinc-800" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-zinc-900 text-zinc-500">
                  Or continue with
                </span>
              </div>
            </div>

            {/* Google */}
            <button
              onClick={handleGoogleSignIn}
              className="w-full flex items-center justify-center gap-3 py-3 bg-white text-black rounded-lg"
            >
              <img
                src="https://www.svgrepo.com/show/475656/google-color.svg"
                className="w-5 h-5"
                alt="Google"
              />
              Continue with Google
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
